import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '@/services/dashboardService';
import { useAuthorization } from '@/hooks/useAuthorization';
import { deneignementService } from '@/services/deneignementService';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PrioriteBadge } from '@/components/common/PrioriteBadge';
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  EyeIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

/**
 * Page tableau de bord
 */
export function DashboardPage() {
  // Filtre par date/tranche de dates (demande utilisateur) : le comportement
  // par defaut (undefined -> l'API applique "aujourd'hui") reste inchange ;
  // ces champs ne sont renseignes que si l'utilisateur choisit explicitement
  // une date ou une periode.
  const [dateDebut, setDateDebut] = useState<string | undefined>(undefined);
  const [dateFin, setDateFin] = useState<string | undefined>(undefined);
  const filtreActif = dateDebut !== undefined;
  const { canAccess } = useAuthorization();

  const appliquerPeriode = (debut: string, fin: string) => {
    setDateDebut(debut);
    setDateFin(fin);
  };

  const reinitialiser = () => {
    setDateDebut(undefined);
    setDateFin(undefined);
  };

  // Charger les statistiques (du jour par defaut, ou de la periode choisie)
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['dashboard-stats', dateDebut, dateFin],
    queryFn: () => dashboardService.getStats(dateDebut, dateFin),
    refetchInterval: 30000, // Rafraichir toutes les 30 secondes
  });

  // Charger les interventions recentes (triees par creation, pas par priorite : sinon
  // une intervention "Normale" tout juste creee peut ne jamais apparaitre ici tant
  // qu'il existe 5 interventions Critique/Urgente, quelle que soit leur anciennete)
  const { data: recentInterventions, isLoading: isLoadingRecent } = useQuery({
    queryKey: ['deneignements', 'recent'],
    queryFn: () => deneignementService.list({ sortBy: 'recent' }, 1, 5),
    refetchInterval: 30000,
  });

  const statCards = [
    {
      name: filtreActif ? 'Interventions de la periode' : 'Interventions du jour',
      value: stats?.interventions_du_jour ?? '-',
      icon: ClipboardDocumentListIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'En cours',
      value: stats ? (stats.en_cours + stats.en_deplacement) : '-',
      icon: TruckIcon,
      color: 'bg-orange-500',
    },
    {
      name: 'Terminees',
      value: stats?.terminees ?? '-',
      icon: CheckCircleIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Urgentes / Critiques',
      value: stats ? `${stats.urgentes} / ${stats.critiques}` : '-',
      icon: ExclamationTriangleIcon,
      color: 'bg-red-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Titre */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Tableau de bord</h2>
        {canAccess.creationDeneignement && (
          <Link to="/deneignements/nouveau" className="btn-primary">
            + Nouveau deneigement
          </Link>
        )}
      </div>

      {/* Filtre par date / tranche de dates - barre compacte sur une seule
          ligne (inputs a largeur intrinseque, pas "w-full" comme .input par
          defaut, sans quoi le bloc s'etalait sur plusieurs lignes) */}
      <div className="flex flex-wrap items-center gap-2">
        <CalendarDaysIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          type="date"
          value={dateDebut ?? ''}
          onChange={(e) => appliquerPeriode(e.target.value, dateFin ?? e.target.value)}
          className="input w-auto py-1 text-sm"
        />
        <span className="text-sm text-gray-400">→</span>
        <input
          type="date"
          value={dateFin ?? ''}
          min={dateDebut}
          onChange={(e) => appliquerPeriode(dateDebut ?? e.target.value, e.target.value)}
          disabled={!dateDebut}
          className="input w-auto py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400"
        />
        <div className="flex items-center gap-1 ml-1">
          <button onClick={() => appliquerPeriode(todayIso(), todayIso())} className="btn-secondary text-xs px-2 py-1">
            Aujourd'hui
          </button>
          <button onClick={() => appliquerPeriode(isoDaysAgo(7), todayIso())} className="btn-secondary text-xs px-2 py-1">
            7j
          </button>
          <button onClick={() => appliquerPeriode(isoDaysAgo(30), todayIso())} className="btn-secondary text-xs px-2 py-1">
            30j
          </button>
        </div>
        {filtreActif && (
          <button onClick={reinitialiser} className="text-xs text-primary-600 hover:underline">
            Reinitialiser
          </button>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <div key={stat.name} className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {isLoadingStats ? (
                      <span className="animate-pulse">...</span>
                    ) : (
                      stat.value
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Details des statuts */}
      {stats && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">
              Repartition par statut
            </h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{stats.en_attente}</p>
                <p className="text-sm text-gray-500">En attente</p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <p className="text-2xl font-bold text-orange-600">{stats.affectee}</p>
                <p className="text-sm text-gray-500">Affectee</p>
              </div>
              <div className="text-center p-3 bg-violet-50 rounded-lg">
                <p className="text-2xl font-bold text-violet-600">{stats.en_deplacement}</p>
                <p className="text-sm text-gray-500">En deplacement</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{stats.en_cours}</p>
                <p className="text-sm text-gray-500">En cours</p>
              </div>
              <div className="text-center p-3 bg-lime-50 rounded-lg">
                <p className="text-2xl font-bold text-lime-600">{stats.cloturee}</p>
                <p className="text-sm text-gray-500">Cloturee</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{stats.validee}</p>
                <p className="text-sm text-gray-500">Validee</p>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-600">{stats.annulee}</p>
                <p className="text-sm text-gray-500">Annulee</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interventions recentes */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">
            Interventions recentes
          </h3>
        </div>
        <div className="card-body p-0">
          {isLoadingRecent ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
              <p className="text-sm text-gray-500 mt-3">Chargement des interventions recentes...</p>
            </div>
          ) : recentInterventions?.data.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Aucune intervention recente.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>N Ordre</th>
                    <th>Date</th>
                    <th>Parking</th>
                    <th>Priorite</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recentInterventions?.data.map((intervention) => (
                    <tr key={intervention.id}>
                      <td className="font-medium">{intervention.numeroOrdre}</td>
                      <td>
                        {new Date(intervention.dateIntervention).toLocaleDateString('fr-FR')}
                      </td>
                      <td>{intervention.parking?.code || '-'}</td>
                      <td>
                        <PrioriteBadge priorite={intervention.priorite} size="sm" />
                      </td>
                      <td>
                        <StatusBadge statut={intervention.statut} size="sm" />
                      </td>
                      <td>
                        <Link
                          to={`/deneignements/${intervention.id}`}
                          className="text-primary-500 hover:text-primary-600"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-4 text-center border-t">
            <Link
              to="/deneignements"
              className="text-primary-500 hover:text-primary-600 font-medium"
            >
              Voir toutes les interventions
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
